import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import GoogleSignInButton from '../components/auth/GoogleSignInButton';
import MicrosoftSignInButton from '../components/auth/MicrosoftSignInButton';
import toast from 'react-hot-toast';
import { Eye, EyeOff } from 'lucide-react';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
const MICROSOFT_ENABLED = Boolean(
  process.env.REACT_APP_MICROSOFT_CLIENT_ID && process.env.REACT_APP_MICROSOFT_TENANT_ID
);
const LOGIN_BRAND_BACKGROUND = `linear-gradient(135deg, rgba(0, 136, 191, 0.92) 0%, rgba(0, 104, 148, 0.96) 55%, rgba(13, 27, 42, 0.94) 100%), url(${process.env.PUBLIC_URL}/brand/images/abstract-futuristic-design-featuring-polygonal-shapes-dark-blue-background.jpg)`;

/**
 * Split login screen: Kakau branding plus password, Google, and Microsoft sign-in.
 */
function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const { login, loginWithGoogle, loginWithMicrosoft } = useAuth();
  const navigate = useNavigate();

  /**
   * Signs in with the existing email and password form.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.success) {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Erro no login:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Exchanges a federated ID token for the portal session.
   */
  const handleFederatedCredential = async (signIn) => {
    setLoading(true);
    try {
      const result = await signIn();
      if (result.success) {
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-screen">
      <div className="login-wrapper">
        <div className="login-branding" style={{ backgroundImage: LOGIN_BRAND_BACKGROUND }}>
          <h2>KakauTech</h2>
          <p>Operações, arquivos e integrações em um só lugar.</p>
          <div className="brand-features">
            <div className="feature">
              <div className="feature-icon" aria-hidden>↑</div>
              <span>Upload e processamento de arquivos</span>
            </div>
            <div className="feature">
              <div className="feature-icon" aria-hidden>⇄</div>
              <span>Envios e consulta de apólices</span>
            </div>
            <div className="feature">
              <div className="feature-icon" aria-hidden>☰</div>
              <span>Usuários e estrutura organizacional</span>
            </div>
            <div className="feature">
              <div className="feature-icon" aria-hidden>✓</div>
              <span>Senha, Google @kakau.co ou Microsoft @kakau.com.br</span>
            </div>
          </div>
        </div>

        <div className="login-form-wrapper">
          <img src="/brand/logos/Logo_Kakau_Tech_-03.png" alt="Kakau Tech" className="login-logo" />

          {GOOGLE_CLIENT_ID ? (
            <GoogleSignInButton
              onCredential={(credential) => handleFederatedCredential(() => loginWithGoogle(credential))}
              onError={() => toast.error('Falha ao abrir o login do Google.')}
            />
          ) : null}

          {MICROSOFT_ENABLED ? (
            <MicrosoftSignInButton
              disabled={loading}
              onCredential={(credential) => handleFederatedCredential(() => loginWithMicrosoft(credential))}
              onError={(detail) => toast.error(detail || 'Falha ao abrir o login da Microsoft.')}
            />
          ) : null}

          {GOOGLE_CLIENT_ID || MICROSOFT_ENABLED ? (
            <div className="social-login-divider">
              <span>OU</span>
            </div>
          ) : null}

          <form onSubmit={handleSubmit}>
            <div className="login-field">
              <label htmlFor="email">E-mail</label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                disabled={loading}
              />
            </div>

            <div className="login-field">
              <label htmlFor="password">Senha</label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Senha"
                  disabled={loading}
                  className="pr-10"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
            </div>

            <button type="submit" disabled={loading} className="btn-submit">
              {loading ? 'Entrando...' : 'Entrar'}
            </button>
          </form>

          <p className="login-alt-link">
            Ou{' '}
            <button type="button" onClick={() => navigate('/register')}>
              crie uma nova conta
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
